/**
 * SEVO Cart JavaScript
 * Handles cart drawer interactions and AJAX cart updates
 */

(function() {
  'use strict';

  const selectors = {
    cartDrawer: '[data-cart-drawer]',
    cartOverlay: '[data-cart-overlay]',
    cartClose: '[data-cart-close]',
    cartToggle: '[data-cart-toggle]',
    cartContent: '[data-cart-content]',
    cartItems: '[data-cart-items]',
    cartEmpty: '[data-cart-empty]',
    cartFooter: '[data-cart-footer]',
    cartCount: '[data-cart-count]',
    cartSubtotal: '[data-cart-subtotal]',
    quantityChange: '[data-quantity-change]',
    removeItem: '[data-remove-item]',
    addToCart: '[data-add-to-cart]',
    productForm: 'form[action*="/cart/add"], form.product-form, [data-product-form]',
    discountToggle: '.cart-drawer__discount-toggle',
    discountForm: '#cart-drawer-discount-form',
    discountInput: '#cart-drawer-discount-input',
    applyDiscount: '[data-apply-discount]',
    discountMessage: '[data-discount-message]',
    cartUpsell: '[data-cart-upsell]',
    bundleBadge: '[data-bundle-badge]'
  };

  const classes = {
    open: 'is-open',
    visible: 'is-visible',
    loading: 'loading',
    noScroll: 'no-scroll'
  };

  class Cart {
    constructor() {
      this.drawer = document.querySelector(selectors.cartDrawer);
      this.overlay = document.querySelector(selectors.cartOverlay);
      this.previousFocus = null; // Track focus for restoration

      // Always bind form events even if drawer isn't found yet
      // (drawer might load later or the form handlers are still needed)
      this.bindEvents();
    }

    bindEvents() {
      // Only bind document-level events once
      if (!this.eventsInitialized) {
        this.eventsInitialized = true;

        // Open cart events
        document.addEventListener('cart:open', () => this.open());

        // Escape key to close
        document.addEventListener('keydown', (e) => {
          if (e.key === 'Escape' && this.isOpen()) {
            this.close();
          }
        });

        // Add to cart buttons - use event delegation for dynamically added elements
        document.addEventListener('click', (e) => {
          try {
            const btn = e.target.closest(selectors.addToCart);
            if (btn) {
              this.handleAddToCart(e, btn);
            }

            // Also handle quantity change and remove via delegation
            const qtyBtn = e.target.closest(selectors.quantityChange);
            if (qtyBtn) {
              this.handleQuantityChange(e, qtyBtn);
            }

            const removeBtn = e.target.closest(selectors.removeItem);
            if (removeBtn) {
              this.handleRemoveItem(e, removeBtn);
            }
          } catch (err) {
            // Error handled silently in production
          }
        });

        // Product forms - use event delegation for better reliability
        document.addEventListener('submit', (e) => {
          const form = e.target.closest(selectors.productForm);
          if (form) {
            this.handleFormSubmit(e, form);
          }
        });
      }

      // These can be rebound when cart content changes
      this.bindDrawerEvents();
    }

    bindDrawerEvents() {
      // Close cart events
      const closeBtn = document.querySelector(selectors.cartClose);
      if (closeBtn && !closeBtn.dataset.bound) {
        closeBtn.dataset.bound = 'true';
        closeBtn.addEventListener('click', () => this.close());
      }

      if (this.overlay && !this.overlay.dataset.bound) {
        this.overlay.dataset.bound = 'true';
        this.overlay.addEventListener('click', () => this.close());
      }

      // Discount code toggle
      const discountToggle = document.querySelector(selectors.discountToggle);
      const discountForm = document.querySelector(selectors.discountForm);
      if (discountToggle && discountForm && !discountToggle.dataset.bound) {
        discountToggle.dataset.bound = 'true';
        discountToggle.addEventListener('click', () => {
          const isExpanded = discountToggle.getAttribute('aria-expanded') === 'true';
          discountToggle.setAttribute('aria-expanded', !isExpanded);
          discountForm.style.display = isExpanded ? 'none' : 'block';
          if (!isExpanded) {
            const input = document.querySelector(selectors.discountInput);
            if (input) input.focus();
          }
        });
      }

      // Apply discount button
      const applyBtn = document.querySelector(selectors.applyDiscount);
      if (applyBtn && !applyBtn.dataset.bound) {
        applyBtn.dataset.bound = 'true';
        applyBtn.addEventListener('click', () => this.handleApplyDiscount());
      }

      // Allow Enter key to apply discount
      const discountInput = document.querySelector(selectors.discountInput);
      if (discountInput && !discountInput.dataset.bound) {
        discountInput.dataset.bound = 'true';
        discountInput.addEventListener('keydown', (e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            this.handleApplyDiscount();
          }
        });
      }
    }

    isOpen() {
      return this.drawer?.classList.contains(classes.open) || false;
    }

    open() {
      // Re-query drawer in case it was added to DOM after init
      if (!this.drawer) {
        this.drawer = document.querySelector(selectors.cartDrawer);
        this.overlay = document.querySelector(selectors.cartOverlay);
      }

      if (!this.drawer) {
        return;
      }

      // Store current focus for restoration
      this.previousFocus = document.activeElement;

      this.drawer.classList.add(classes.open);
      this.drawer.setAttribute('aria-hidden', 'false');
      if (this.overlay) {
        this.overlay.classList.add(classes.visible);
      }
      document.body.classList.add(classes.noScroll);

      // Focus the close button for accessibility (use rAF for timing)
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const closeBtn = this.drawer?.querySelector(selectors.cartClose);
          if (closeBtn && document.body.contains(closeBtn)) {
            closeBtn.focus();
          }
        });
      });

      // Announce to screen readers
      this.announceToScreenReader('Cart opened');
    }

    close() {
      if (!this.drawer) return;

      this.drawer.classList.remove(classes.open);
      this.drawer.setAttribute('aria-hidden', 'true');
      if (this.overlay) {
        this.overlay.classList.remove(classes.visible);
      }
      document.body.classList.remove(classes.noScroll);

      // Restore focus to previously focused element
      if (this.previousFocus && typeof this.previousFocus.focus === 'function') {
        this.previousFocus.focus();
      }

      // Announce to screen readers
      this.announceToScreenReader('Cart closed');
    }

    /**
     * Announce message to screen readers via live region
     */
    announceToScreenReader(message) {
      let announcer = document.getElementById('sr-announcer');
      if (!announcer) {
        announcer = document.createElement('div');
        announcer.id = 'sr-announcer';
        announcer.setAttribute('role', 'status');
        announcer.setAttribute('aria-live', 'polite');
        announcer.setAttribute('aria-atomic', 'true');
        announcer.className = 'visually-hidden';
        document.body.appendChild(announcer);
      }
      announcer.textContent = message;
    }

    async handleQuantityChange(e, btn) {
      btn = btn || e.currentTarget;
      const key = btn.dataset.key;
      const action = btn.dataset.action;
      const quantityContainer = btn.closest('.cart-item__quantity');
      const quantityEl = quantityContainer?.querySelector('[data-quantity-value]');

      if (!quantityEl || !key) return;

      let currentQuantity = parseInt(quantityEl.textContent) || 1;
      let quantity = currentQuantity;

      if (action === 'increase') {
        quantity++;
      } else if (action === 'decrease') {
        quantity = Math.max(0, currentQuantity - 1);

        // Check for tier drop when decreasing
        if (quantity < currentQuantity) {
          const currentCartCount = window.SEVO.cartCount || 0;
          if (!this.confirmTierDrop(currentCartCount, currentQuantity - quantity)) {
            return; // User cancelled
          }
        }
      }

      await this.updateItem(key, quantity);
    }

    async handleRemoveItem(e, btn) {
      btn = btn || e.currentTarget;
      const key = btn.dataset.key;
      if (!key) return;

      // Check for tier drop when removing item
      const currentCartCount = window.SEVO.cartCount || 0;
      const itemContainer = btn.closest('[data-cart-item]');
      const quantityEl = itemContainer?.querySelector('[data-quantity-value]');
      const itemQuantity = quantityEl ? parseInt(quantityEl.textContent) || 1 : 1;

      if (!this.confirmTierDrop(currentCartCount, itemQuantity)) {
        return; // User cancelled
      }

      await this.updateItem(key, 0);
    }

    async handleAddToCart(e, btn) {
      btn = btn || e.currentTarget;
      const variantId = btn.dataset.addToCart;

      // Check if button is inside a product form
      const parentForm = btn.closest('form');

      // If inside a product form, handle submission directly here (more reliable than waiting for submit event)
      if (parentForm && (parentForm.action?.includes('/cart/add') || parentForm.classList.contains('product-form') || parentForm.hasAttribute('data-product-form'))) {
        e.preventDefault();
        e.stopPropagation();
        await this.handleFormSubmit(e, parentForm);
        return;
      }

      // Standalone button (not in form) - needs variant ID
      if (!variantId) {
        return;
      }

      e.preventDefault();
      btn.classList.add(classes.loading);

      try {
        await this.addItem(variantId, 1);
        this.open();
      } catch (error) {
        this.showToast(error.message || 'Unable to add item to cart. Please try again.', 'error');
      } finally {
        btn.classList.remove(classes.loading);
      }
    }

    async handleFormSubmit(e, form) {
      e.preventDefault();
      form = form || e.currentTarget;
      const submitBtn = form.querySelector('[data-add-to-cart]') || form.querySelector('[type="submit"]');
      const formData = new FormData(form);

      if (submitBtn) {
        submitBtn.classList.add(classes.loading);
        submitBtn.disabled = true;
      }

      try {
        // Check if routes are available
        const cartAddUrl = window.SEVO?.routes?.cart_add_url || '/cart/add';

        const response = await fetch(cartAddUrl + '.js', {
          method: 'POST',
          body: formData
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.description || 'Add to cart failed');
        }

        await this.refreshCart();
        this.open();
      } catch (error) {
        this.showToast(error.message || 'Unable to add item to cart. Please try again.', 'error');
      } finally {
        if (submitBtn) {
          submitBtn.classList.remove(classes.loading);
          submitBtn.disabled = false;
        }
      }
    }

    async addItem(variantId, quantity) {
      const cartAddUrl = window.SEVO?.routes?.cart_add_url || '/cart/add';

      try {
        const response = await fetch(cartAddUrl + '.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            id: variantId,
            quantity: quantity
          })
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.description || 'Unable to add item to cart');
        }

        await this.refreshCart();
      } catch (error) {
        throw new Error(error.message || 'Unable to add item to cart. Please try again.');
      }
    }

    async updateItem(key, quantity) {
      const cartChangeUrl = window.SEVO?.routes?.cart_change_url || '/cart/change';

      try {
        const response = await fetch(cartChangeUrl + '.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            id: key,
            quantity: quantity
          })
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.description || 'Unable to update cart');
        }

        await this.refreshCart();
      } catch (error) {
        this.showCartError(error.message || 'Unable to update cart. Please try again.');
      }
    }

    async refreshCart() {
      // Guard against overlapping refresh requests (e.g. rapid add-to-cart clicks)
      if (this._refreshing) {
        return this._refreshing;
      }

      this._refreshing = this._doRefreshCart().finally(() => {
        this._refreshing = null;
      });

      return this._refreshing;
    }

    async _doRefreshCart() {
      try {
        const cartUrl = window.SEVO?.routes?.cart_url || '/cart';
        const response = await fetch(cartUrl + '.js');
        const cart = await response.json();

        this.updateCartCount(cart.item_count);
        this.updateCartSubtotal(cart.total_price);

        // Update bundle UI based on new item count
        this.updateBundleUI(cart.item_count);

        // Refresh cart drawer content from Shopify section
        const sectionId = 'cart-drawer';
        const drawerResponse = await fetch(`/?section_id=${sectionId}`);

        if (!drawerResponse.ok) {
          throw new Error('Failed to fetch cart section');
        }

        const html = await drawerResponse.text();

        // Validate response contains expected section wrapper
        if (!html.includes('data-cart-drawer') && !html.includes('data-cart-content')) {
          throw new Error('Invalid cart section response');
        }

        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');

        const newContent = doc.querySelector(selectors.cartContent);
        const newFooter = doc.querySelector(selectors.cartFooter);
        const currentContent = document.querySelector(selectors.cartContent);
        const currentFooter = document.querySelector(selectors.cartFooter);

        // Safely update content by replacing child nodes
        if (newContent && currentContent) {
          // Clear existing content
          while (currentContent.firstChild) {
            currentContent.removeChild(currentContent.firstChild);
          }
          // Clone and append new nodes (safer than innerHTML)
          Array.from(newContent.childNodes).forEach(node => {
            currentContent.appendChild(node.cloneNode(true));
          });
          // Note: No need to rebind events - we use event delegation now
        }

        if (newFooter && currentFooter) {
          // Update existing footer
          while (currentFooter.firstChild) {
            currentFooter.removeChild(currentFooter.firstChild);
          }
          Array.from(newFooter.childNodes).forEach(node => {
            currentFooter.appendChild(node.cloneNode(true));
          });
        } else if (newFooter && !currentFooter) {
          // Footer doesn't exist yet (cart was empty) - add it
          const drawer = document.querySelector(selectors.cartDrawer);
          if (drawer) {
            drawer.appendChild(newFooter.cloneNode(true));
          }
        } else if (!newFooter && currentFooter) {
          // Cart is now empty - remove footer
          currentFooter.remove();
        }

      } catch (error) {
        this.showCartError('Unable to update cart. Please try again.');
      }
    }

    /**
     * Show a temporary error message in the cart drawer
     */
    showCartError(message) {
      const existingError = this.drawer?.querySelector('.cart-drawer__error');
      if (existingError) existingError.remove();

      const errorEl = document.createElement('div');
      errorEl.className = 'cart-drawer__error';

      // Use safe DOM methods instead of innerHTML
      const messageEl = document.createElement('p');
      messageEl.textContent = message;
      errorEl.appendChild(messageEl);

      const retryBtn = document.createElement('button');
      retryBtn.type = 'button';
      retryBtn.className = 'cart-drawer__error-retry';
      retryBtn.textContent = 'Refresh Page';
      retryBtn.addEventListener('click', () => window.location.reload());
      errorEl.appendChild(retryBtn);

      const content = this.drawer?.querySelector(selectors.cartContent);
      if (content) {
        content.insertBefore(errorEl, content.firstChild);

        // Announce error to screen readers
        this.announceToScreenReader(message);

        // Auto-remove after 10 seconds with warning
        setTimeout(() => {
          if (errorEl.parentNode) {
            this.announceToScreenReader('Error message dismissed');
            errorEl.remove();
          }
        }, 10000);
      }
    }

    /**
     * Show a toast notification (replaces browser alert())
     */
    showToast(message, type = 'info') {
      // Remove existing toast
      const existingToast = document.querySelector('.sevo-toast');
      if (existingToast) existingToast.remove();

      const toast = document.createElement('div');
      toast.className = `sevo-toast sevo-toast--${type}`;
      toast.setAttribute('role', 'alert');
      toast.setAttribute('aria-live', 'assertive');

      const messageEl = document.createElement('span');
      messageEl.className = 'sevo-toast__message';
      messageEl.textContent = message;
      toast.appendChild(messageEl);

      const closeBtn = document.createElement('button');
      closeBtn.type = 'button';
      closeBtn.className = 'sevo-toast__close';
      closeBtn.setAttribute('aria-label', 'Dismiss');
      closeBtn.innerHTML = '&times;';
      closeBtn.addEventListener('click', () => toast.remove());
      toast.appendChild(closeBtn);

      document.body.appendChild(toast);

      // Trigger animation
      requestAnimationFrame(() => {
        toast.classList.add('is-visible');
      });

      // Auto-remove after 5 seconds
      setTimeout(() => {
        toast.classList.remove('is-visible');
        setTimeout(() => toast.remove(), 300);
      }, 5000);
    }

    updateCartCount(count) {
      document.querySelectorAll(selectors.cartCount).forEach(el => {
        el.textContent = count;
        if (count > 0) {
          el.style.display = '';
        } else {
          el.style.display = 'none';
        }
      });
      window.SEVO.cartCount = count;
    }

    updateCartSubtotal(total) {
      document.querySelectorAll(selectors.cartSubtotal).forEach(el => {
        el.textContent = this.formatMoney(total);
      });
    }

    formatMoney(cents) {
      const format = window.SEVO.moneyFormat || '${{amount}}';
      const value = (cents / 100).toFixed(2);
      return format.replace('{{amount}}', value).replace('{{amount_no_decimals}}', Math.round(cents / 100));
    }

    /**
     * Get bundle tier discount based on item count
     * Uses config from window.SEVO.bundleTiers (set in theme.liquid)
     */
    getTierDiscount(count) {
      const tiers = window.SEVO?.bundleTiers || { 2: 5, 3: 10, 4: 15 };

      // Find highest applicable tier
      let discount = 0;
      for (const [threshold, percent] of Object.entries(tiers)) {
        if (count >= parseInt(threshold)) {
          discount = percent;
        }
      }
      return discount;
    }

    /**
     * Update bundle UI elements (upsell messages, badges)
     * Uses config from window.SEVO.bundleTiers
     */
    updateBundleUI(itemCount) {
      const tiers = window.SEVO?.bundleTiers || { 2: 5, 3: 10, 4: 15 };
      const tierPercent = this.getTierDiscount(itemCount);
      const nextTierPercent = this.getTierDiscount(itemCount + 1);

      // Update upsell message
      const upsell = document.querySelector(selectors.cartUpsell);
      if (upsell) {
        if (nextTierPercent > tierPercent) {
          // There's a better tier available
          const upsellText = document.createElement('span');
          if (tierPercent > 0) {
            upsellText.innerHTML = `Add 1 more for <strong>${nextTierPercent}% off</strong> (currently ${tierPercent}%)`;
          } else {
            upsellText.innerHTML = `Add 1 more item for <strong>${nextTierPercent}% off</strong>`;
          }
          upsell.innerHTML = '';
          upsell.appendChild(upsellText);
          upsell.style.display = 'block';
        } else {
          upsell.style.display = 'none';
        }
      }

      // Update bundle badge
      const badge = document.querySelector(selectors.bundleBadge);
      if (badge) {
        if (tierPercent > 0) {
          const badgeText = badge.querySelector('.cart-bundle-badge__text');
          if (badgeText) {
            badgeText.textContent = `Bundle Discount: ${tierPercent}% applied`;
          }
          badge.style.display = 'flex';
        } else {
          badge.style.display = 'none';
        }
      }
    }

    /**
     * Check if removing an item would reduce the bundle tier
     * Returns true if user confirms, false if cancelled
     */
    confirmTierDrop(currentCount, quantityToRemove) {
      const newCount = currentCount - quantityToRemove;
      const currentTier = this.getTierDiscount(currentCount);
      const newTier = this.getTierDiscount(newCount);

      if (newTier < currentTier) {
        return confirm(`Removing this will reduce your discount from ${currentTier}% to ${newTier}%. Continue?`);
      }
      return true;
    }

    async handleApplyDiscount() {
      const input = document.querySelector(selectors.discountInput);
      const message = document.querySelector(selectors.discountMessage);

      if (!input || !message) return;

      const code = input.value.trim();
      if (!code) {
        message.textContent = 'Please enter a discount code.';
        message.className = 'cart-drawer__discount-message error';
        return;
      }

      const applyBtn = document.querySelector(selectors.applyDiscount);
      if (applyBtn) {
        applyBtn.disabled = true;
        applyBtn.textContent = 'Applying...';
      }

      try {
        // Store discount code and redirect to checkout with code applied
        // Shopify doesn't have a client-side API for discount validation
        // The code gets applied at checkout via URL parameter
        const baseCheckoutUrl = window.SEVO?.routes?.checkout_url || '/checkout';
        const checkoutUrl = baseCheckoutUrl + '?discount=' + encodeURIComponent(code);

        // Show success message and redirect
        message.textContent = 'Code applied! Redirecting to checkout...';
        message.className = 'cart-drawer__discount-message success';

        // Small delay to show message before redirect
        setTimeout(() => {
          window.location.href = checkoutUrl;
        }, 500);

      } catch (error) {
        message.textContent = 'Failed to apply discount code.';
        message.className = 'cart-drawer__discount-message error';

        if (applyBtn) {
          applyBtn.disabled = false;
          applyBtn.textContent = 'Apply';
        }
      }
    }
  }

  // Initialize cart
  function initCart() {
    window.SEVO = window.SEVO || {};
    const cart = new Cart();
    window.SEVO.Cart = cart;

    // Expose openDrawer method for external use (bundle builder, etc.)
    window.SEVO.Cart.openDrawer = function() {
      cart.open();
    };
  }

  // Initialize when DOM is ready, or immediately if already loaded
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCart);
  } else {
    // DOM already loaded
    initCart();
  }
})();
